/**
 * EventHealthBadge Component
 * 
 * Displays the health status of the event system
 * Shows HEALTHY (green) or UNHEALTHY (red) with icon
 * 
 * @component
 * @example
 * <EventHealthBadge status="HEALTHY" />
 * <EventHealthBadge status="UNHEALTHY" showText={false} />
 */

import { type FC } from 'react';
import clsx from 'clsx';
import { MdOutlineCheckCircle, MdOutlineError } from '@/config/icons';
import { FiRefreshCw } from '@/config/icons';
import type { EventHealthBadgeProps } from './EventHealthBadge.types';
import styles from './EventHealthBadge.module.css';

export const EventHealthBadge: FC<EventHealthBadgeProps> = ({
  status,
  showText = true,
  className,
  isLoading = false,
}) => {
  // Loading state
  if (isLoading || !status) {
    return (
      <div
        className={clsx(styles.healthBadge, styles.loading, className)}
        role="status"
        aria-label="Carregando status de saúde"
      >
        <span className={styles.icon}>
          <FiRefreshCw />
        </span>
        {showText && <span className={styles.text}>Carregando...</span>}
      </div>
    );
  }

  const isHealthy = status === 'HEALTHY';

  const icon = isHealthy ? <MdOutlineCheckCircle /> : <MdOutlineError />;
  const text = isHealthy ? 'Sistema Saudável' : 'Sistema com Problemas';
  const ariaLabel = isHealthy 
    ? 'Sistema de eventos funcionando normalmente' 
    : 'Sistema de eventos com problemas detectados';

  return (
    <div
      className={clsx(
        styles.healthBadge,
        isHealthy ? styles.healthy : styles.unhealthy,
        className
      )}
      role="status"
      aria-label={ariaLabel}
    >
      <span className={styles.icon} aria-hidden="true">
        {icon}
      </span>
      {showText && <span className={styles.text}>{text}</span>}
    </div>
  );
};

export default EventHealthBadge;
